import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { querySelectorAllWithDepthSorted } from "../tools/dom.tools";

export const CUSTOM_ELEMENT_ROOT = "isCustomElementRoot";// this attribute is set on the webComponent root element
export const CUSTOM_ELEMENT_WAS_SLOTTED = "customElementWasSlotted";

/**
 * Bind all children slots
 * This is an 'imitation' of the standard slot that can be used when element is in the light DOM (ie not shadow DOM)
 * https://developer.mozilla.org/fr/docs/Web/Web_Components/Using_templates_and_slots
 * ! This is experimental
 * * If the binding is incorrect you can set use a "slotParentRef" attribute on the slot element and set the ref value of the component to force the binding.
 */
export function bindChildrenSlots(wc: HTMLElement): void {
    setProp(wc, CUSTOM_ELEMENT_ROOT, true);// this is useful to identify the first ancestor with slots for a component

    // get all slots in the component
    const slots = wc.querySelectorAll("slot");
    if (slots.length === 0) {
        return;
    }

    const slotsByName = {};
    slots.forEach((slot) => {
        // find all elements to append within slot
        setProp(slotsByName, slot.name, slot);
    });
    _bindChildrenToSlots(wc, slotsByName);
}

/**
 * Binds elements with slot="slotName" to <slot name="slotName"> in elementToBrowse
 */
function _bindChildrenToSlots(elementToBrowse: Element, slotsByName: { [name: string]: HTMLSlotElement }): void {
    const querySelector = Object.keys(slotsByName).map((slotName) => `[slot='${slotName}']`).join(",");
    const slotElements = querySelectorAllWithDepthSorted(elementToBrowse, querySelector);
    if (slotElements.length === 0) {
        return;// there is no corresponding element for these slots in elementToBrowse
    }

    // browse elements by depth (Breadth First Search)
    for (const e of slotElements) {
        // console.debug("evaluate", e.elt.getAttribute("debugId"), e.depth, "as child of", elementToBrowse.getAttribute("debugId") + "[" + e.elt.getAttribute("slot") + "]");

        const slotParentRef = e.elt.getAttribute("slotParentRef");
        if (slotParentRef && slotParentRef !== elementToBrowse.getAttribute("ref")) {
            // slot element has a "slotParentRef" attribute and its parent is not elementToBrowse => will be attached to another component
            continue;
        }

        let isChild = true;
        let parent = e.elt.parentElement;
        while (parent && parent !== elementToBrowse) {
            if (getProp(parent, CUSTOM_ELEMENT_WAS_SLOTTED) || getProp(parent, CUSTOM_ELEMENT_ROOT)) {
                // this element is the child of another element with slots
                // or its parent was slotted and should not be considered
                isChild = false;
                break;
            }
            parent = parent.parentElement;
        }
        if (isChild) {
            // re-parent element inside slot
            const slotName = e.elt.getAttribute("slot");
            if (!slotName) { continue; }
            const slot = slotsByName[slotName];
            if (!slot) { continue; }

            // console.debug(e.elt.getAttribute("debugId"), "is child of", elementToBrowse.getAttribute("debugId") + "[" + slotName + "]");
            setProp(e.elt, CUSTOM_ELEMENT_WAS_SLOTTED, true);
            slot.appendChild(e.elt);
        }
    }
}